/**
 * ShiftManagementPage — `/mail/shift`.
 *
 * Live operator surface backed by service-mail `/v1/shifts/*`.
 *
 * Two stacked panels:
 *   - Operator self panel: shows current shift (clock in/out CTA + elapsed
 *     timer) and own history.
 *   - Manager panel: currently-on-duty list (filterable by outlet) + full
 *     history table. The Mail/manageShifts permission gates these calls;
 *     a 403 from the API surfaces inline as a friendly message.
 */
import { type JSX, useEffect, useState } from 'react';
import { Clock, LogIn, LogOut, Loader2 } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import {
  clockIn,
  clockOut,
  listActiveShifts,
  listMyShifts,
  listShifts,
  type ShiftDto,
} from '../../lib/api/shifts';

interface ShiftManagementPageProps {
  /** Test-injectable clock for the elapsed timer. */
  readonly clock?: () => number;
}

export function ShiftManagementPage({ clock = () => Date.now() }: ShiftManagementPageProps): JSX.Element {
  const [current, setCurrent] = useState<ShiftDto | null>(null);
  const [myHistory, setMyHistory] = useState<readonly ShiftDto[]>([]);
  const [active, setActive] = useState<readonly ShiftDto[] | null>(null);
  const [allShifts, setAllShifts] = useState<readonly ShiftDto[] | null>(null);
  const [outletCode, setOutletCode] = useState('NG-LAG-VI-001');
  const [filterOutlet, setFilterOutlet] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [managerError, setManagerError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(clock());

  async function refreshSelf(): Promise<void> {
    try {
      const res = await listMyShifts({ pageSize: 10 });
      setCurrent(res.currentShift ?? null);
      setMyHistory(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load shifts');
    }
  }

  async function refreshManager(): Promise<void> {
    try {
      const [actives, all] = await Promise.all([
        listActiveShifts(filterOutlet || undefined),
        listShifts({
          ...(filterOutlet && { outletCode: filterOutlet }),
          pageSize: 25,
        }),
      ]);
      setActive(actives);
      setAllShifts(all.items);
      setManagerError(null);
    } catch (e) {
      setManagerError(e instanceof Error ? e.message : 'Manager view requires Mail/manageShifts');
      setActive([]);
      setAllShifts([]);
    }
  }

  useEffect(() => {
    void refreshSelf();
  }, []);

  useEffect(() => {
    void refreshManager();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [filterOutlet]);

  useEffect(() => {
    if (!current) return undefined;
    const id = setInterval(() => setNow(clock()), 1_000);
    return () => clearInterval(id);
  }, [current, clock]);

  async function handleClockIn(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await clockIn({ outletCode });
      await refreshSelf();
      await refreshManager();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clock in failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClockOut(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await clockOut({});
      await refreshSelf();
      await refreshManager();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clock out failed');
    } finally {
      setBusy(false);
    }
  }

  const elapsedMs = current ? Math.max(0, now - new Date(current.clockInAt).getTime()) : 0;
  const hours = Math.floor(elapsedMs / 3_600_000);
  const minutes = Math.floor((elapsedMs % 3_600_000) / 60_000);
  const seconds = Math.floor((elapsedMs % 60_000) / 1000);

  return (
    <div className="space-y-5">
      <PageHeader title="Shift Management" description="Clock in / clock out, manager view of on-duty staff." />

      {error && <div className="bg-danger-light border border-danger text-danger text-sm rounded-xl p-3">{error}</div>}

      <div className="bg-white border border-border rounded-xl p-6" data-testid="shift-self-panel">
        <div className="flex items-center gap-3 mb-3">
          <Clock className="w-5 h-5 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Current shift</h3>
        </div>

        {!current ? (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-muted">You are not clocked in.</p>
            <div className="inline-flex items-center gap-2">
              <label className="text-xs text-muted" htmlFor="shift-outlet">Outlet</label>
              <input
                id="shift-outlet"
                type="text"
                value={outletCode}
                onChange={(e) => setOutletCode(e.target.value)}
                className="border border-border rounded px-3 py-1.5 text-sm font-mono"
                data-testid="shift-outlet-input"
              />
            </div>
            <div>
              <button
                type="button"
                onClick={() => void handleClockIn()}
                disabled={busy || !outletCode}
                className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
                data-testid="shift-clock-in-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Clock in
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4" data-testid="shift-active-panel">
            <p className="text-xs text-muted">Elapsed at {current.outletCode}</p>
            <p className="text-4xl font-bold text-gray-900 font-mono mt-2" data-testid="shift-elapsed">
              {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </p>
            <p className="text-xs text-muted mt-2">
              Clocked in at {new Date(current.clockInAt).toLocaleTimeString()}
            </p>
            <button
              type="button"
              onClick={() => void handleClockOut()}
              disabled={busy}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
              data-testid="shift-clock-out-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Clock out
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-xl p-5" data-testid="shift-my-history">
        <h3 className="font-semibold text-gray-900 mb-3">My recent shifts</h3>
        {myHistory.length === 0 ? (
          <p className="text-xs text-muted">No shifts yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-muted">
              <tr>
                <th className="text-left py-1">Outlet</th>
                <th className="text-left py-1">Clock in</th>
                <th className="text-left py-1">Clock out</th>
                <th className="text-left py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {myHistory.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="py-1 font-mono text-xs">{s.outletCode}</td>
                  <td className="py-1 text-xs">{new Date(s.clockInAt).toLocaleString()}</td>
                  <td className="py-1 text-xs">{s.clockOutAt ? new Date(s.clockOutAt).toLocaleString() : '—'}</td>
                  <td className="py-1 text-xs">{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-border rounded-xl p-5" data-testid="shift-manager-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Manager view — on duty now</h3>
          <input
            type="text"
            placeholder="Filter outlet"
            value={filterOutlet}
            onChange={(e) => setFilterOutlet(e.target.value)}
            className="border border-border rounded px-3 py-1 text-xs font-mono"
            data-testid="shift-manager-filter"
          />
        </div>
        {managerError && <p className="text-xs text-muted mb-2">{managerError}</p>}
        {active && active.length > 0 ? (
          <ul className="text-sm space-y-1" data-testid="shift-active-list">
            {active.map((s) => (
              <li key={s.id} className="flex justify-between border-b border-border py-1">
                <span className="font-mono text-xs">{s.operatorId.slice(0, 16)} @ {s.outletCode}</span>
                <span className="text-xs text-muted">since {new Date(s.clockInAt).toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted">{active === null ? 'Loading…' : 'No-one is on duty.'}</p>
        )}

        <h4 className="font-semibold text-gray-900 mt-5 mb-2 text-sm">All shifts (most recent)</h4>
        {allShifts && allShifts.length > 0 ? (
          <table className="w-full text-sm" data-testid="shift-all-table">
            <thead className="text-[10px] uppercase text-muted">
              <tr>
                <th className="text-left py-1">Operator</th>
                <th className="text-left py-1">Outlet</th>
                <th className="text-left py-1">Clock in</th>
                <th className="text-left py-1">Clock out</th>
                <th className="text-left py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {allShifts.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="py-1 font-mono text-xs">{s.operatorId.slice(0, 16)}</td>
                  <td className="py-1 font-mono text-xs">{s.outletCode}</td>
                  <td className="py-1 text-xs">{new Date(s.clockInAt).toLocaleString()}</td>
                  <td className="py-1 text-xs">{s.clockOutAt ? new Date(s.clockOutAt).toLocaleString() : '—'}</td>
                  <td className="py-1 text-xs">{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-muted">{allShifts === null ? 'Loading…' : 'No shifts yet.'}</p>
        )}
      </div>
    </div>
  );
}
